import { useCallback, useState } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { acquireApiToken } from '@/auth/acquireApiToken';
import type { CreateQrCodePayload, QrCodeRecord } from './types';

interface CreateResult {
  data?: QrCodeRecord;
  error?: { code: string; message: string };
}

export function useCreateQrCode(): {
  create: (payload: CreateQrCodePayload) => Promise<CreateResult>;
  loading: boolean;
} {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(false);

  const create = useCallback(
    async (payload: CreateQrCodePayload): Promise<CreateResult> => {
      if (!isAuthenticated || accounts.length === 0) {
        return { error: { code: 'not_authenticated', message: 'Sign in to create QR codes' } };
      }
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        return { error: { code: 'config_missing', message: 'REACT_APP_API_URL is not set' } };
      }

      setLoading(true);
      try {
        const tokenResponse = { accessToken: await acquireApiToken(instance, accounts[0]) };
        const headers = {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
          'Content-Type': 'application/json',
        };
        const res = await fetch(`${apiUrl}/admin/qr-codes`, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        });
        const json = await res.json();
        if (!res.ok) {
          return {
            error: {
              code: json.error || `http_${res.status}`,
              message: json.message || json.error || `HTTP ${res.status}`,
            },
          };
        }
        return { data: json.data as QrCodeRecord };
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Failed to create QR code';
        return { error: { code: 'network_error', message: msg } };
      } finally {
        setLoading(false);
      }
    },
    [isAuthenticated, instance, accounts],
  );

  return { create, loading };
}
